import React, { useEffect, useState, FC } from "react";
import queryString from "query-string";
import { getDraws } from "../../../../services/draws";
import {
  DrawSheetItem,
  FullKnockOutSheet,
  KnockOutSheet,
  Selected
} from "../types";
import { getWinnerScore } from "../utils";
import "./index.scss";

interface Props {
  title?: string;
  eventClassificationCode?: string;
  matchTypeCode?: string;
  dates?: string;
  surface?: string;
  inDoorOrOutDoor?: string;
}

interface queryParams {
  eventClassificationCode?: string;
  matchTypeCode?: string;
  drawsheetStructureCode?: string;
  playerTypeCode?: string;
  tournamentId?: string;
  circuitCode?: string;
}

// Notes
// Link example https://foo?eventclassificationcode=M&matchtypecode=S&tenniscatecode=WT&tournamentid=1100189160

const PrintPage: FC<Props> = ({
  title,
  dates,
  surface,
  inDoorOrOutDoor,
  eventClassificationCode,
  matchTypeCode
}: Props) => {
  const [results, setResults] = useState<
    {
      rounds: (
        | KnockOutSheet
        | {
            roundDesc: string;
            matches: DrawSheetItem[];
          })[];
      groupName: string;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [queryParams, setQueryParams] = useState<queryParams>({});

  useEffect(() => {
    document.getElementsByTagName("html")[0].classList.add("print-version");
    (async () => {
      setIsLoading(true);
      const query: queryParams = queryString.parse(location.search);
      setQueryParams(query);
      const params = Object.keys(query).reduce(
        (list: Selected, key: string) => {
          if (query[key]) {
            list[key] = query[key];
          }
          return list;
        },
        {}
      );
      try {
        const {
          koGroups
        }: { koGroups: FullKnockOutSheet[] | null } = await getDraws(
          params,
          query && query.tournamentId ? +query.tournamentId : 0,
          query.circuitCode
        );

        const results = koGroups
          ? koGroups.map(item => ({
              ...item,
              rounds: [
                ...item.rounds,
                {
                  roundDesc: "Winner",
                  matches: item.rounds[item.rounds.length - 1].matches
                }
              ]
            }))
          : [];

        setResults(results);
      } finally {
        setIsLoading(false);
      }
    })();
    return () => {
      document
        .getElementsByTagName("html")[0]
        .classList.remove("print-version");
    };
  }, []);

  const entryStatusList = ["Q", "WC"];

  const matchType = {
    S: "Singles",
    D: "Doubles"
  };

  const eventClassification = {
    M: "Main Draw",
    Q: "Qualifying Draw"
  };

  const WTT = ["MT", "WT", "JT"];
  const isOlympics = ["Paris 2024"];
  const searchParams = new URLSearchParams(location.search);

  return (
    <div className="print-page">
      {isLoading ? null : (
        <>
          <a onClick={() => window.print()} className="print-page__btn">
            Print drawsheet
          </a>
          {isOlympics.filter(val => (title || "").indexOf(val) > -1).length ? (
            <div
              className={`print-page__logo print-page__logo--${(title || "")
                .replace(/ /gi, "")
                .toLocaleLowerCase()}`}
            ></div>
          ) : !!queryParams.circuitCode &&
            !!WTT.includes(queryParams.circuitCode) &&
            searchParams.get("olympics") !== "true" ? (
            <div className="print-page__logo"></div>
          ) : null}
          <div className="print-page__title">
            <h2>
              {title} ({dates}) -{" "}
              {matchTypeCode ? matchType[matchTypeCode] : null} -{" "}
              {eventClassificationCode
                ? eventClassification[eventClassificationCode]
                : null}{" "}
              - ({surface} - {inDoorOrOutDoor})
            </h2>
          </div>
          <div className="print-page__drawsheet">
            {results.map(({ rounds }: FullKnockOutSheet, groupKey: number) => (
              <div
                key={`group-${groupKey}`}
                className="print-page__drawsheet--section"
              >
                {rounds.map(({ roundDesc, matches }, roundKey: number) => (
                  <div
                    key={`group-${groupKey}-round-${roundKey}`}
                    className="print-page__drawsheet--round"
                  >
                    <div className="print-page__drawsheet--caption">
                      {roundDesc}
                    </div>
                    <div
                      className={`print-page__drawsheet--matches round-${roundKey} round-${roundDesc.toLocaleLowerCase()}`}
                    >
                      {matches.map(({ matchId, teams, resultStatusCode }) => (
                        <div
                          key={matchId}
                          className="print-page__drawsheet--block"
                        >
                          {teams
                            .filter(({ isWinner }) =>
                              rounds.length - 1 === roundKey ? isWinner : true
                            )
                            .map(
                              (
                                { entryStatus, players, seeding, isWinner },
                                teamKey: number
                              ) => (
                                <div
                                  key={`matchId-${teamKey}`}
                                  className={`print-page__drawsheet--player-${
                                    !teamKey ? "top" : "bottom"
                                  }`}
                                >
                                  {isWinner &&
                                  rounds.length - 1 !== roundKey ? (
                                    <div className="print-page__drawsheet--score">
                                      {getWinnerScore(teams)
                                        .filter(
                                          ({ scoreA, scoreB }) =>
                                            scoreA && scoreB
                                        )
                                        .map(
                                          (
                                            { scoreA, scoreB },
                                            scoreKey: number
                                          ) => (
                                            <span key={scoreKey}>
                                              {scoreA !== null
                                                ? `${scoreA.score}${
                                                    scoreA.losingScore !== null
                                                      ? `(${scoreA.losingScore})`
                                                      : ""
                                                  }`
                                                : null}
                                              -
                                              {scoreB !== null
                                                ? `${scoreB.score}${
                                                    scoreB.losingScore !== null
                                                      ? `(${scoreB.losingScore})`
                                                      : ""
                                                  }`
                                                : null}
                                              &nbsp;
                                            </span>
                                          )
                                        )}
                                    </div>
                                  ) : null}
                                  <div
                                    className={`print-page__drawsheet--block-${
                                      players.filter(player => player).length >
                                      1
                                        ? "double"
                                        : "single"
                                    }`}
                                  >
                                    {players.map((player, playerKey) =>
                                      player ? (
                                        <div
                                          key={player.playerId}
                                          className="print-page__drawsheet--block-text"
                                        >
                                          {playerKey !== 0 ? "/ " : ""}
                                          <span>{player.givenName}</span>
                                          <span>
                                            {player.familyName.toLocaleUpperCase()}
                                          </span>
                                          <span>({player.nationality})</span>
                                          {seeding
                                            ? `[${seeding}]`
                                            : entryStatus &&
                                              entryStatusList.includes(
                                                entryStatus
                                              )
                                            ? `(${entryStatus})`
                                            : null}
                                        </div>
                                      ) : resultStatusCode === "BYE" ? (
                                        <div className="print-page__drawsheet--block-text BYE">
                                          BYE
                                        </div>
                                      ) : null
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PrintPage;
